import { useState, useEffect } from 'react';

// mui
import { TableContainer, Table, TableBody, TableRow, TableCell, Skeleton, Collapse, Box } from '@mui/material';

// api
import { FilterRecommendList } from '../../api';

// client-shared
import { FilterItem, FilterDescHeader, ContainerScroll } from '@goldblack/client-comps';
import { storage } from '@goldblack/client-storage';
import { hash, str, search } from '@goldblack/client-utils';

function descFromSaved(saved) {
    const m = {};
    for (const f of saved ?? []) if (f.searchDisplay) m[f.label || f.name] = f.searchDisplay;
    return m;
}

function RecommendFilterCollapse({ open, loading, filterTypes, filters, actions }) {
    return (
        <Box
            sx={{
                flex: 1,
                minHeight: 0,
                maxHeight: '100%',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
            }}>
            <Collapse
                in={open}
                timeout='auto'
                unmountOnExit={false}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                    maxHeight: '100%',
                    overflow: 'hidden',
                    '& .MuiCollapse-wrapper': {
                        maxHeight: '100%',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                    },
                    '& .MuiCollapse-wrapperInner': {
                        maxHeight: '100%',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                    },
                }}>
                <Box
                    sx={{
                        height: '100%',
                        minHeight: 0,
                        maxHeight: '100%',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                    }}>
                    <ContainerScroll sx={{ height: '100%', maxHeight: '100%', flex: 1, minHeight: 0 }}>
                        {loading ? (
                            <Skeleton variant='rectangular' height={26 * 2} sx={{ m: 1 }} />
                        ) : (
                            <TableContainer sx={{ p: 1 }}>
                                <Table padding='checkbox'>
                                    <TableBody>
                                        {filterTypes.map((filter, index) => (
                                            <TableRow key={filter.name}>
                                                <TableCell
                                                    sx={{
                                                        width: '20%',
                                                        borderBottom: 'none',
                                                        color: 'primary.main',
                                                    }}>
                                                    {filter.label || filter.name}
                                                </TableCell>
                                                <TableCell align='right' sx={{ borderBottom: 'none' }}>
                                                    <FilterItem
                                                        options='searchOptions'
                                                        group='recommend'
                                                        filterType={filter}
                                                        filters={filters}
                                                        actions={actions}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </ContainerScroll>
                </Box>
            </Collapse>
        </Box>
    );
}

function RecommendFilter(props) {
    const { filter } = storage.transience.getValue();
    const [filterDesc, setFilterDesc] = useState(() =>
        descFromSaved(storage.localStorage.getValue('filter.recommend')),
    );
    const [filterData, setFilterData] = useState({ loading: false });

    const buildFilterDesc = () => {
        const byName = {};
        const byId = {};
        for (const f of filter.recommend.selected) {
            const filterType = filter.recommend.data.find((x) => x._id === f.filterId);
            if (!filterType) continue;
            const displays = JSON.parse(f.display);
            const validCount = displays.filter((x) => Boolean(x)).length;
            if (validCount !== filterType.searchOptions.length) continue;
            const display = str.formatString(filterType.searchDisplay, ...displays);
            if (display) {
                const label = filterType.label || filterType.name;
                byName[label] = display;
                byId[f.filterId] = { display, label };
            }
        }
        return { byName, byId };
    };

    const makeSubKey = () => {
        const countryEntry = filter.recommend.selected.find((f) => /country/i.test(f.name));
        const genderEntry = filter.recommend.selected.find((f) => /gender/i.test(f.name));

        let country = null;
        let gender = null;

        if (countryEntry && countryEntry.hashValue !== countryEntry.filterId) {
            try {
                const values = JSON.parse(countryEntry.value);
                country = search.normalizeTag(values);
            } catch {}
        }

        if (genderEntry && genderEntry.hashValue !== genderEntry.filterId) {
            try {
                const values = JSON.parse(genderEntry.value);
                if (Array.isArray(values) && values[0]) gender = values[0];
            } catch {}
        }

        if (country && gender) return `${country}:${gender}`.toLowerCase();
        if (gender) return `global:${gender}`.toLowerCase();
        if (country) return `${country}:all`.toLowerCase();
        return 'global';
    };

    const updateRecommendFilter = (filterType, setValue, values, inputValues, displays) => {
        let nextHashValue = '';

        if (setValue) {
            const valueString = search.normalizeTag(values);
            if (valueString) {
                nextHashValue = hash.fnv1aHash(valueString);
            }
        }

        const newFilter = {
            filterId: filterType._id,
            name: filterType.name,
            value: JSON.stringify(values),
            inputValue: JSON.stringify(inputValues),
            display: JSON.stringify(displays),
            hashValue: nextHashValue !== '' ? nextHashValue : filterType._id,
        };
        let callback = (type) => type.filterId === newFilter.filterId;
        storage.transience.updateState('filter.recommend.selected', callback, newFilter);
    };

    const onChange = (filterType, setValue, values, inputValues, displays) => {
        updateRecommendFilter(filterType, setValue, values, inputValues, displays);
    };

    useEffect(() => {
        if (filter.recommend.data.length === 0) {
            FilterRecommendList(undefined, setFilterData);
        }
        if (filter.recommend.data.length > 0) {
            const { byName, byId } = buildFilterDesc();
            setFilterDesc(byName);
            const selected = filter.recommend.selected
                .filter((f) => {
                    try {
                        return JSON.parse(f.value)[0] !== '';
                    } catch {
                        return false;
                    }
                })
                .map((f) =>
                    byId[f.filterId]
                        ? { ...f, searchDisplay: byId[f.filterId].display, label: byId[f.filterId].label }
                        : f,
                );
            storage.localStorage.setState('filter.recommend', selected);
        }
        props.setType?.('recommend');
        props.setFilters?.(makeSubKey());
    }, [filter.recommend.selected]);

    return (
        <Box
            sx={{
                flexShrink: 1,
                minHeight: 0,
                maxHeight: '100%',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
            }}>
            <Box sx={{ flexShrink: 0 }}>
                <FilterDescHeader filterDesc={filterDesc} open={props.open} onToggle={props.onToggle} />
            </Box>
            <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                <RecommendFilterCollapse
                    open={props.open}
                    loading={filterData.loading}
                    filterTypes={filter.recommend.data}
                    filters={filter.recommend.selected}
                    actions={{ onChange }}
                />
            </Box>
        </Box>
    );
}

export { RecommendFilter };
